import React from 'react';
import { useLanguage } from '@/App';
import { ScrollReveal } from '@/components/ScrollReveal';

const WhyPersona = () => {
  const { language } = useLanguage();

  const content = {
    tr: {
      title: 'Farkımız',
      titleHighlight: 'Nedir?',
      subtitle: 'Farkımız sezgisel değil, yapısal.',
      subtitleLine1: 'Çoğu kampanyada bir influencer seçilir, içerik paylaşılır ve sonuçlar beklenir.',
      subtitleLine2: 'Markalar genelde \'umarım iyi performans alırız\' der.',
      subtitleLine3: 'Ancak milyonlarca liralık bir kanalın \'umarım\' ile yönetilmemesi gerekir.',
      cards: [
        {
          badge: 'NİŞ UZMANLIK',
          title: 'Anne & Ev Kadını Uzmanıyız',
          quote: 'Uzmanlığımız Anne-Bebek, Anne-Çocuk ve Ev Kadını Influencerlar.',
          description: 'Biz her markaya hitap eden, her influencer kategorisinde çalışan bir ajans değiliz.\n\nBiz bir konuda uzmanlaştık:\nAnne influencer\'lar ve çoklu influencer kampanya yönetimi.'
        },
        {
          badge: 'OPARA SİSTEMİ',
          title: 'Veriyle Konuşuyoruz',
          quote: 'Kampanya başlamadan, sonuç projeksiyonları masada.',
          description: 'Global Influencer Analiz platformu OPARA ile güçlendirilmiş sistemimiz sayesinde:\n\nBir influencer\'ın güncel metriklerini (reel paylaşım sıklığı, görünürlük performansı, iş birlikleri, ortalama reel izlenme ve etkileşim oranı) ve o influencer ile çalışırsak etkileşim oranı, reel izlenmesi, izlenme başına maliyet, story izlenmesi ve story link tıklanma öngörülerini sunuyoruz.\n\nInfluencer pazarlamayı veriye dayalı seçimlerle ve kampanya başarı kriterlerini bilerek yola çıkılan bir kanala çeviriyoruz.'
        },
        {
          badge: 'TAM YÖNETİM',
          title: 'Uçtan Uca Tüm Süreci Yönetiyoruz',
          quote: 'Siz stratejinize odaklanın, tüm yükü biz sırtlanalım.',
          description: 'Yüzlerce anne influencer ile çalıştık. Bazen bir tanesiyle bir içerik ürettik, bazen ise aynı anda birçoğu ile büyük kampanyalar yürüttük. Her defasında tüm operasyonu baştan sona üstleniyoruz:',
          bullets: [
            'Influencer iletişimi',
            'İçerik fikri, taslak ve storyboard',
            'Çekim planlaması',
            'Revize ve kurgu süreçleri',
            'Yayın takvimi ve takip',
            'Sözleşme ve ödeme süreçleri',
            'Performans raporları'
          ],
          footer: 'Influencer pazarlamada başarı, veriye dayalı doğru kişileri seçmekle başlar.\nHatasız ve profesyonel süreç yönetimiyle gelir.'
        },
        {
          badge: 'ŞEFFAF MODEL',
          title: 'Şeffaf ve Adil İlerliyoruz',
          quote: 'Yağmacı kâr marjları değil, hakkaniyetli kazanç.',
          description: 'Influencer gerçek maliyetlerinin üzerine fahiş marjlar koyarak markaları faturalandıran ve her defasında sadece temsil ettiği influencer\'ları öneren modeli reddediyoruz.\n\nPeki biz nasıl çalışıyoruz?',
          bullets: [
            'Marka talebine ve bütçesine en uygun kimse, o influencer\'ları öneriyoruz.',
            'Genelde herhangi bir ajansa bağlı olmayan, direkt iletişime geçebildiğimiz influencer\'lar ile çalışıyoruz. Maliyetleri minimuma indirip aynı bütçeyle çok daha fazlasını markalarımıza sunabiliyoruz.',
            'Fiyatlandırmamız şeffaf, adil ve sürdürülebilir:'
          ],
          subBullets: [
            'Influencer ücretlerini olduğu gibi markaya aktarıyoruz',
            'Hizmet bedelimizi ayrıca ve açıkça belirtiyoruz',
            'Marka tam olarak neye, ne kadar ödediğini biliyor'
          ],
          footer: 'Bizim amacımız tek seferlik iş yapmak değil, markalarla uzun vadeli ve güvene dayalı bir iş ortaklığı kurmak.'
        }
      ]
    },
    en: {
      title: 'What\'s Our',
      titleHighlight: 'Difference?',
      subtitle: 'Our difference is not intuitive, it\'s structural.',
      subtitleLine1: 'In most campaigns, an influencer is selected, content is shared, and results are awaited.',
      subtitleLine2: 'Brands usually say \'hopefully we\'ll get good performance.\'',
      subtitleLine3: 'However, a channel worth millions shouldn\'t be managed with \'hopefully.\'',
      cards: [
        {
          badge: 'NICHE EXPERTISE',
          title: 'Mom & Housewife Experts',
          quote: 'Our expertise is Mom-Baby, Mom-Child and Housewife Influencers.',
          description: 'We are not an agency that appeals to every brand and works in every influencer category.\n\nWe specialized in one thing:\nMom influencers and multi-influencer campaign management.'
        },
        {
          badge: 'OPARA SYSTEM',
          title: 'We Speak with Data',
          quote: 'Result projections on the table before the campaign starts.',
          description: 'Thanks to our system powered by the Global Influencer Analysis platform OPARA:\n\nWe provide an influencer\'s current metrics (reel posting frequency, visibility performance, collaborations, average reel views and engagement rate) and if we work with that influencer, engagement rate, reel views, cost per view, story views and story link click projections.\n\nWe turn influencer marketing into a channel where data-driven selections and campaign success criteria are known from the start.'
        },
        {
          badge: 'FULL MANAGEMENT',
          title: 'We Manage the Entire Process End-to-End',
          quote: 'Focus on your strategy, let us carry all the burden.',
          description: 'We have worked with hundreds of mom influencers. Sometimes we produced one content with one, sometimes we ran big campaigns with many at the same time. Each time we take over the entire operation from start to finish:',
          bullets: [
            'Influencer communication',
            'Content idea, draft and storyboard',
            'Shooting planning',
            'Revision and editing processes',
            'Publication calendar and tracking',
            'Contract and payment processes',
            'Performance reports'
          ],
          footer: 'Success in influencer marketing starts with choosing the right people based on data.\nIt comes with flawless and professional process management.'
        },
        {
          badge: 'TRANSPARENT MODEL',
          title: 'We Progress Transparently and Fairly',
          quote: 'Not predatory profit margins, but fair earnings.',
          description: 'We reject the model that invoices brands by putting excessive margins on top of influencer real costs and recommending only the influencers it represents each time.\n\nSo how do we work?',
          bullets: [
            'We recommend the influencers that best fit the brand\'s request and budget.',
            'We generally work with influencers who are not affiliated with any agency and whom we can contact directly. We minimize costs and offer much more to our brands with the same budget.',
            'Our pricing is transparent, fair and sustainable:'
          ],
          subBullets: [
            'We transfer influencer fees as-is to the brand',
            'We state our service fee separately and clearly',
            'The brand knows exactly what they are paying for and how much'
          ],
          footer: 'Our goal is not to do one-time business, but to establish a long-term and trust-based business partnership with brands.'
        }
      ]
    }
  };

  const t = content[language];

  // Card sizes - increasing from left to right
  const cardSizes = [
    'min-h-[280px]',  // Card 1 - smallest
    'min-h-[320px]',  // Card 2
    'min-h-[400px]',  // Card 3
    'min-h-[440px]'   // Card 4 - largest
  ];

  return (
    <section className="py-12 bg-black bg-opacity-75" data-testid="why-persona-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <ScrollReveal>
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-4xl font-bold">
              <span className="text-white">{t.title} </span>
              <span className="text-[#FFDD59]">{t.titleHighlight}</span>
            </h2>
            <p className="text-gray-400 mt-2 md:mt-3 text-sm md:text-lg">
              {t.subtitle}
            </p>
            <div className="mt-2 md:mt-3 text-xs md:text-base max-w-3xl mx-auto space-y-1 px-2">
              <p className="text-gray-300">{t.subtitleLine1}</p>
              <p className="text-gray-300">{t.subtitleLine2}</p>
              <p className="text-[#F5C518] italic">{t.subtitleLine3}</p>
            </div>
          </div>
        </ScrollReveal>

        {/* Cards Grid - 4 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 items-start">
          {t.cards.map((card, index) => (
            <ScrollReveal key={index} delay={index * 100}>
              <div 
                className={`bg-gray-900 border border-gray-700 rounded-xl p-4 md:p-5 hover:border-[#FFDD59] transition-colors duration-300 card-hover-glow ${cardSizes[index]}`}
              >
                {/* Badge */}
                <span className="inline-block bg-[#FFDD59] text-black text-xs font-bold px-2 py-1 rounded-full mb-3 uppercase tracking-wider">
                  {card.badge}
                </span>
                
                {/* Title */}
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                  {card.title}
                </h3>
                
                {/* Quote */}
                <p className="text-[#FFDD59]/75 italic text-xs mb-3 border-l-2 border-[#FFDD59] pl-2">
                  {card.quote}
                </p>
                
                {/* Description */}
                <div className="text-gray-300 text-xs leading-relaxed space-y-2">
                  {card.description.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="whitespace-pre-line">{paragraph}</p>
                  ))}
                  
                  {/* Bullets if exist */}
                  {card.bullets && (
                    <ul className="space-y-1 mt-2">
                      {card.bullets.map((bullet, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-[#FFDD59]">✔</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {/* Sub-bullets if exist */}
                  {card.subBullets && (
                    <ul className="space-y-1 mt-1 ml-4">
                      {card.subBullets.map((bullet, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-gray-500">-</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {/* Footer if exists */}
                  {card.footer && (
                    <p className="mt-2 whitespace-pre-line font-medium text-white/90">{card.footer}</p>
                  )}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyPersona;
